import { Box } from "@mui/material";
import { OverflowTooltip, Table } from "@4uhub/lib4uhub";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { IProfileListClassification } from "../../../../../models/profile-list-classification";
import { ProfileListClassificationsService } from "../../../../../services/profileListClassifications.service";
import { ProfileListClassification } from "./ProfileListClassificationForm/ProfileListClassification";

const translationPath = "page.register.roles.";

const ProfileListClassificationsPage = () => {
  const { id: roleId } = useParams();
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [rows, setRows] = useState<IProfileListClassification[]>([]);

  const service = useMemo(() => new ProfileListClassificationsService(), []);

  const columns: GridColDef<IProfileListClassification>[] = [
    {
      field: "classificationCode",
      headerName: t(translationPath + "code") || "Code",
      width: 100,
      sortable: false,
      renderCell: (params) => params.row.listClassification?.code ?? "-",
    },
    {
      field: "listClassification",
      headerName: t(translationPath + "list_classification") || "Classification",
      flex: 1,
      renderCell: (params) =>
        params.value ? (
          <OverflowTooltip
            text={params.value.name}
            tooltip={params.value.name}
            textTransform="none"
            variant="body2"
          />
        ) : (
          "-"
        ),
    },
  ];

  const handleAdd = () => {
    setSelectedId(undefined);
    setShowForm(true);
  };

  const handleRowClick = (params: GridRowParams<IProfileListClassification>) => {
    if (!params.row) return;
    setSelectedId(params.row.id);
    setShowForm(true);
  };

  const handleBack = () => {
    setSelectedId(undefined);
    setShowForm(false);
  };

  const params = useMemo(() => ({ RoleId: roleId }), [roleId]);

  if (!roleId) return null;

  return (
    <Box sx={{ height: 600, width: "100%" }}>
      {showForm ? (
        <Box
          sx={(theme) => ({
            p: 1,
            height: "100%",
            overflow: "auto",
            backgroundColor: theme.palette.background.default,
            borderRadius: 1,
          })}
        >
          <ProfileListClassification
            id={selectedId}
            roleId={roleId}
            rows={rows}
            onBack={handleBack}
          />
        </Box>
      ) : (
        <Box height={600}>
          <Table<IProfileListClassification>
            showDefaultMacroFilters={false}
            service={service}
            serviceProps={params}
            columns={columns}
            title={t(translationPath + "list_classifications") || "List classifications"}
            searchInput
            onAdd={handleAdd}
            onRowClick={handleRowClick}
            onFetchData={(data) => {
              setRows(data);
            }}
            defaultPageSize={20}
            pageSizeOptions={[5, 10, 20]}
            confirmOptions={{
              title:
                t(translationPath + "linked_list_classification_remove_title") ||
                "Confirm Deletion",
              description: t(
                translationPath + "linked_list_classification_remove_description"
              ),
              confirmationButtonProps: {
                variant: "contained",
                color: "error",
              },
            }}
            searchInputPlaceHolder={
              t(translationPath + "search_list_classification") || "Search classification..."
            }
            addButtonLabel={
              t(translationPath + "add_list_classification") || "Add classification"
            }
          />
        </Box>
      )}
    </Box>
  );
};

export default memo(ProfileListClassificationsPage);
